import {
  EntKind,
  type TransitionsV3BreakdownOptions,
  type TransitionsV3Filters,
} from '@revelio/data-access';

import {
  selectionIdToWorkforceSegment,
  workforceSegmentToLegacyBoolean,
  workforceSegmentToWorkforceType,
} from '../../../../shared/user-preferences/utils/workforce-segment';
import { type Filter, FilterId } from '../types/filters';
import { getStaticListFilter } from './get-static-list-filter';

/**
 * The deepest active geography filter, mapped to the breakdown level below it.
 * The last place the hierarchy is hardcoded — composition derives its own from
 * the active taxonomy's `selectionListIds`.
 *
 * Only `country` genuinely needs `usesStateBreakdownOverMsa`: `state` and `county` exist
 * solely on the administrative taxonomy, so their presence already identifies
 * it, whereas a country filter is valid on both and its next tier differs.
 */
const getGeoBreakdownLevel = (
  filters: TransitionsV3Filters,
  usesStateBreakdownOverMsa: boolean
): EntKind => {
  if (filters.county?.length) return EntKind.County;
  // The tier below state is county, not city.
  if (filters.state?.length) return EntKind.County;
  if (filters.city?.length || filters.msa?.length) return EntKind.City;
  if (filters.country?.length) {
    return usesStateBreakdownOverMsa ? EntKind.State : EntKind.Msa;
  }
  if (filters.region?.length) return EntKind.Country;
  return EntKind.Region;
};

const getRicsBreakdownLevel = (filters: TransitionsV3Filters): EntKind => {
  if (filters.rics_k400?.length || filters.rics_k50?.length)
    return EntKind.RicsK400;
  if (filters.rics_k10?.length) return EntKind.RicsK50;
  return EntKind.RicsK10;
};

/** We want role levels to match the filter role levels (1 / 3 / 5 / 7) */
const getRoleBreakdownLevel = (filters: TransitionsV3Filters): EntKind => {
  if (
    filters.role_level_7?.length ||
    filters.role_level_6?.length ||
    filters.role_level_5?.length
  )
    return EntKind.RoleLvl_7;
  if (filters.role_level_4?.length || filters.role_level_3?.length)
    return EntKind.RoleLvl_5;
  if (filters.role_level_2?.length || filters.role_level_1?.length)
    return EntKind.RoleLvl_3;
  return EntKind.RoleLvl_1;
};

export const getTransitionsV3BreakdownOptions = ({
  filters,
  transitionsFilters,
  usesStateBreakdownOverMsa,
}: {
  filters: Filter[];
  transitionsFilters: TransitionsV3Filters;
  usesStateBreakdownOverMsa: boolean;
}): TransitionsV3BreakdownOptions => {
  const contingentWorkersFilter = getStaticListFilter({
    filters,
    filterId: FilterId.CONTINGENT_WORKERS,
  });
  const workforceSegment = selectionIdToWorkforceSegment(
    contingentWorkersFilter?.selections?.[0]?.id
  );

  return {
    with_contingent_workers: workforceSegmentToLegacyBoolean(workforceSegment),
    workforce_type: workforceSegmentToWorkforceType(workforceSegment),
    geo_breakdown_level: getGeoBreakdownLevel(
      transitionsFilters,
      usesStateBreakdownOverMsa
    ),
    rics_breakdown_level: getRicsBreakdownLevel(transitionsFilters),
    role_breakdown_level: getRoleBreakdownLevel(transitionsFilters),
  };
};
